import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '../../ui/drawer';

export type ConfirmSheetPresentation = 'auto' | 'drawer' | 'dialog';

export interface ConfirmSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: string;
  /** @default 'Voltar' */
  cancelLabel?: string;
  /** Visual weight of the confirm button. @default 'default' */
  confirmVariant?: 'default' | 'destructive' | 'outline';
  /**
   * Single-action sheet (an explanation with just "Voltar"): hides the cancel
   * button, and the confirm button takes the initial focus instead.
   * @default false
   */
  hideCancel?: boolean;
  onConfirm: () => void;
  /** Cancel handler. When omitted, cancelling just closes (`onOpenChange(false)`). */
  onCancel?: () => void;
  /** Spinner on confirm + cancel disabled while the action runs. */
  confirmLoading?: boolean;
  /**
   * Spinner on cancel + confirm disabled while the CANCEL-side action runs
   * (e.g. a leave prompt whose "discard" has to clear a draft before leaving).
   */
  cancelLoading?: boolean;
  /**
   * Whether outside click / Escape / drag-down may close it. Figma confirmations
   * are NOT dismissible — the user must pick an action. @default false
   */
  dismissible?: boolean;
  /**
   * `auto` (default): centered dialog from 768px up, bottom drawer below.
   * Without `window.matchMedia` (SSR/tests) `auto` resolves to `drawer`.
   */
  presentation?: ConfirmSheetPresentation;
  /**
   * Root testid (on the panel). Derived: `<testid>-title`, `<testid>-description`,
   * `<testid>-confirm`, `<testid>-cancel`.
   */
  'data-testid'?: string;
}

const DESKTOP_QUERY = '(min-width: 768px)';

function canMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function useIsDesktop(enabled: boolean): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(
    () => enabled && canMatchMedia() && window.matchMedia(DESKTOP_QUERY).matches
  );

  useEffect(() => {
    if (!enabled || !canMatchMedia()) return;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const handler = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    setIsDesktop(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Safari < 14
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [enabled]);

  return isDesktop;
}

const titleClass = 'text-base font-medium leading-none tracking-normal text-card-foreground';
const descriptionClass = 'text-sm leading-5 text-muted-foreground';

function preventDismiss(event: Event) {
  event.preventDefault();
}

/**
 * Confirmation sheet used for destructive/leave prompts. Bottom drawer on
 * mobile (Figma "Drawer": handle, left-aligned header, stacked full-width
 * buttons with the confirm action on top), centered dialog on desktop.
 */
function ConfirmSheet({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Voltar',
  confirmVariant = 'default',
  hideCancel = false,
  onConfirm,
  onCancel,
  confirmLoading = false,
  cancelLoading = false,
  dismissible = false,
  presentation = 'auto',
  'data-testid': testId,
}: ConfirmSheetProps) {
  const isDesktop = useIsDesktop(presentation === 'auto');
  const resolved: Exclude<ConfirmSheetPresentation, 'auto'> =
    presentation === 'auto' ? (isDesktop ? 'dialog' : 'drawer') : presentation;

  const derive = (part: string) => (testId ? `${testId}-${part}` : undefined);

  // Vaul does NOT move focus into the drawer by default (`autoFocus=false`), so the
  // trigger behind the overlay would keep focus inside an `aria-hidden` subtree.
  // Focus lands on the safe action (cancel) — never on a destructive confirm.
  // On close, Radix would focus its `DialogTrigger` — there is none here (the
  // sheet is opened programmatically), so we restore the element focused when
  // it opened (e.g. the "Excluir" icon button in a list).
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  function focusCancelOnOpen(event: Event) {
    event.preventDefault();
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
    // Without a cancel button the only action is the safe one.
    (hideCancel ? confirmRef.current : cancelRef.current)?.focus();
  }
  function restoreOpener() {
    const opener = openerRef.current;
    openerRef.current = null;
    if (opener && opener.isConnected) opener.focus();
  }
  // Fired by Radix's FocusScope on unmount (after `hideOthers` has dropped the
  // `aria-hidden` on the rest of the page). Vaul keeps the drawer mounted through
  // its exit animation (~500 ms), so restoring earlier would land the focus inside
  // an `aria-hidden` subtree — the "Voltar" button stays focused (and visible,
  // animating out) until then.
  function restoreOpenerOnClose(event: Event) {
    event.preventDefault();
    restoreOpener();
  }

  function handleOpenChange(next: boolean) {
    if (!next && !dismissible) return;
    onOpenChange(next);
  }

  function handleCancel() {
    if (onCancel) onCancel();
    else onOpenChange(false);
  }

  // Radix warns when a dialog has no description; opt out explicitly then.
  const describedBy = description == null ? { 'aria-describedby': undefined } : {};

  const confirmButton = (
    <Button
      ref={confirmRef}
      type="button"
      variant={confirmVariant}
      className="w-full sm:w-auto"
      onClick={onConfirm}
      loading={confirmLoading}
      disabled={cancelLoading}
      data-testid={derive('confirm')}
    >
      {confirmLabel}
    </Button>
  );

  const cancelButton = hideCancel ? null : (
    <Button
      ref={cancelRef}
      type="button"
      variant="outline"
      className="w-full sm:w-auto"
      onClick={handleCancel}
      loading={cancelLoading}
      disabled={confirmLoading}
      data-testid={derive('cancel')}
    >
      {cancelLabel}
    </Button>
  );

  if (resolved === 'dialog') {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          size="sm"
          data-slot="confirm-sheet"
          data-presentation="dialog"
          data-testid={testId}
          onEscapeKeyDown={dismissible ? undefined : preventDismiss}
          onInteractOutside={dismissible ? undefined : preventDismiss}
          onOpenAutoFocus={focusCancelOnOpen}
          onCloseAutoFocus={restoreOpenerOnClose}
          {...describedBy}
        >
          <DialogHeader className="gap-1.5 px-6 pb-4 pt-6">
            <DialogTitle className={titleClass} data-testid={derive('title')}>
              {title}
            </DialogTitle>
            {description != null && (
              <DialogDescription className={descriptionClass} data-testid={derive('description')}>
                {description}
              </DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter className="px-6 pb-6">
            {cancelButton}
            {confirmButton}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange} dismissible={dismissible}>
      <DrawerContent
        data-slot="confirm-sheet"
        data-presentation="drawer"
        data-testid={testId}
        onEscapeKeyDown={dismissible ? undefined : preventDismiss}
        onOpenAutoFocus={focusCancelOnOpen}
        onCloseAutoFocus={restoreOpenerOnClose}
        {...describedBy}
      >
        <div data-slot="confirm-sheet-header" className="flex flex-col gap-1.5 px-6 py-4 text-left">
          <DrawerTitle className={titleClass} data-testid={derive('title')}>
            {title}
          </DrawerTitle>
          {description != null && (
            <DrawerDescription className={descriptionClass} data-testid={derive('description')}>
              {description}
            </DrawerDescription>
          )}
        </div>
        <div data-slot="confirm-sheet-footer" className={cn('flex flex-col gap-2 p-4')}>
          <Button
            ref={confirmRef}
            type="button"
            variant={confirmVariant}
            className="w-full"
            onClick={onConfirm}
            loading={confirmLoading}
            disabled={cancelLoading}
            data-testid={derive('confirm')}
          >
            {confirmLabel}
          </Button>
          {hideCancel ? null : (
            <Button
              ref={cancelRef}
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleCancel}
              loading={cancelLoading}
              disabled={confirmLoading}
              data-testid={derive('cancel')}
            >
              {cancelLabel}
            </Button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
ConfirmSheet.displayName = 'ConfirmSheet';

export { ConfirmSheet };
